import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import FinanceDateFilter from "../../components/finance/FinanceDateFilter";
import FinanceHelpLabel from "../../components/finance/FinanceHelpLabel";
import FinanceMetricCard from "../../components/finance/FinanceMetricCard";
import FinanceWarningBanner from "../../components/finance/FinanceWarningBanner";
import { RecordLoadingSkeleton } from "../../components/shared/ContentLoadingSkeleton";
import CopyableIdentifier from "../../components/controls/CopyableIdentifier";
import { fetchFinanceCustomerDetails } from "../../finance/customer-api";
import type { FinanceCustomerDetailsResponse } from "../../finance/customer-types";
import {
  financeRangeFromSearch,
  financeRangeSearch,
  type FinanceDateRangeValue,
} from "../../finance/dates";
import { formatMoney, formatPercent, knownMoney } from "../../finance/formatting";

export default function FinanceCustomerDetails() {
  const { customerId } = useParams();
  const [range, setRange] = useState<FinanceDateRangeValue>(() =>
    financeRangeFromSearch(window.location.search),
  );
  const requestedCurrencyId = Number(
    new URLSearchParams(window.location.search).get("currency"),
  );
  const [report, setReport] = useState<FinanceCustomerDetailsResponse | null>(
    null,
  );
  const [currency, setCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const id = Number(customerId);

  useEffect(() => {
    if (!Number.isInteger(id) || id <= 0) {
      setError("Invalid customer.");
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    fetchFinanceCustomerDetails(id, range)
      .then((response) => {
        if (active) {
          setReport(response);
          setCurrency((current) => {
            if (response.profitability.some((item) => item.currency === current)) {
              return current;
            }

            return (
              response.profitability.find(
                (item) => item.currency_id === requestedCurrencyId,
              )?.currency ??
              response.profitability[0]?.currency ??
              ""
            );
          });
        }
      })
      .catch(() => {
        if (active) {
          setError("Customer finance details could not be loaded.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [id, range.from, range.to, requestedCurrencyId]);

  const primary = report?.profitability.find(
    (item) => item.currency === currency,
  );
  const shipments = useMemo(
    () =>
      report?.shipments.filter((shipment) => shipment.currency === currency) ??
      [],
    [currency, report?.shipments],
  );
  const credits = useMemo(
    () =>
      report?.credits.filter(
        (credit) => (credit.currency || "") === currency,
      ) ?? [],
    [currency, report?.credits],
  );

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <a
              className="text-sm font-semibold text-emerald-700 hover:underline"
              href={`/finance/customers${financeRangeSearch(range)}`}
            >
              ← Customer Profitability
            </a>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">
              {report?.customer.name || "Customer Finance"}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {report?.customer.customer_number || "No customer number"} ·{" "}
              {report?.customer.customer_type_name || "No customer type"}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            {report && report.profitability.length > 1 && (
              <label className="text-sm font-medium text-slate-700">
                Currency
                <select
                  className="mt-1 block rounded-lg border border-slate-300 bg-white px-3 py-2"
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                >
                  {report.profitability.map((item) => (
                    <option key={item.currency_id} value={item.currency}>
                      {item.currency}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <FinanceDateFilter value={range} onChange={setRange} />
          </div>
        </div>

        {error && (
          <div className="mt-5">
            <FinanceWarningBanner>{error}</FinanceWarningBanner>
          </div>
        )}
        {!error && loading && (
          <RecordLoadingSkeleton className="mt-5" />
        )}

        {!error && !loading && report && (
          <>
            <section className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <FinanceMetricCard
                label={`Net Sales${primary ? ` (${primary.currency})` : ""}`}
                value={
                  primary
                    ? knownMoney(
                        primary.net_sales,
                        primary.known_net_sales,
                        primary.currency,
                      )
                    : "—"
                }
                incomplete={primary?.net_sales === null}
              />
              <FinanceMetricCard
                label="Material COGS"
                value={
                  primary
                    ? knownMoney(primary.cogs, primary.known_cogs, primary.currency)
                    : "—"
                }
                incomplete={primary?.cogs === null}
              />
              <FinanceMetricCard
                label="Gross Profit"
                value={
                  primary
                    ? knownMoney(
                        primary.gross_profit,
                        primary.known_net_sales - primary.known_cogs,
                        primary.currency,
                      )
                    : "—"
                }
                incomplete={primary?.gross_profit === null}
              />
              <FinanceMetricCard
                label="Gross Margin"
                value={formatPercent(primary?.gross_margin)}
                incomplete={primary?.gross_margin === null}
              />
              <FinanceMetricCard
                label="Return / Credit Rate"
                value={formatPercent(primary?.return_credit_rate)}
              />
            </section>

            <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 p-4">
                <h2 className="font-semibold text-slate-900">Recent shipments</h2>
              </div>
              {shipments.length === 0 ? (
                <p className="p-4 text-sm text-slate-500">
                  No recent shipments for this currency in this period.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Order" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Product / Lot" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Sales" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="COGS" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Gross Profit" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {shipments.map((shipment) => (
                        <tr key={shipment.id}>
                          <td className="px-4 py-4">
                            <a
                              className="font-semibold text-emerald-700 hover:underline"
                              href={`/shipments/${shipment.order_id}`}
                            >
                              {shipment.order_number}
                            </a>
                          </td>
                          <td className="px-4 py-4">
                            {shipment.product_name || shipment.plu}
                            <p className="flex items-center gap-1 text-xs text-slate-500">
                              <span>{shipment.plu} ·</span>
                              <CopyableIdentifier
                                value={shipment.lot_number}
                                description="lot number"
                              />
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            {formatMoney(shipment.sales, shipment.currency)}
                          </td>
                          <td className="px-4 py-4">
                            {formatMoney(shipment.cogs, shipment.currency)}
                          </td>
                          <td className="px-4 py-4">
                            {formatMoney(
                              shipment.gross_profit,
                              shipment.currency,
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 p-4">
                <h2 className="font-semibold text-slate-900">Closed credits</h2>
              </div>
              {credits.length === 0 ? (
                <p className="p-4 text-sm text-slate-500">
                  No closed credits for this currency in this period.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Credit" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Product / Lot" />
                        </th>
                        <th className="px-4 py-3">
                          <FinanceHelpLabel label="Amount" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {credits.map((credit) => (
                        <tr
                          key={`${credit.id}-${credit.plu}-${credit.lot_number}`}
                        >
                          <td className="px-4 py-4">{credit.name}</td>
                          <td className="px-4 py-4">
                            {credit.product_name || credit.plu}
                            <p className="flex items-center gap-1 text-xs text-slate-500">
                              <span>{credit.plu} ·</span>
                              <CopyableIdentifier
                                value={credit.lot_number}
                                description="lot number"
                              />
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            {formatMoney(
                              credit.amount,
                              credit.currency || undefined,
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}
